<template>
  <div class="view-container">
    <!-- 连接切换栏 -->
    <div class="conn-bar">
      <div class="conn-bar-left">
        <i class="fas fa-database"></i>
        <span class="conn-label">当前连接：</span>
        <select v-model="currentConnId" class="conn-select" @change="onSwitchConnection">
          <option v-for="conn in connections" :key="conn.id" :value="conn.id">{{ conn.name }}</option>
        </select>
        <span v-if="currentConn" class="conn-server">{{ currentConn.server }} / {{ currentConn.database }}</span>
        <button class="conn-btn" @click="loadConnections" title="刷新连接"><i class="fas fa-sync-alt"></i></button>
        <button class="conn-btn" @click="showConnModal = true" title="管理连接">
          <i class="fas fa-cog"></i>
        </button>
      </div>
      <div class="conn-bar-right">
        <span v-if="currentConn && currentConn.isDefault" class="conn-tag default-tag">默认</span>
        <span v-if="currentConn && !currentConn.isDefault" class="conn-tag ds-tag">数据源</span>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-area">
      <!-- 左侧对象列表 -->
      <div class="left-panel">
        <div class="panel-header">
          <div class="obj-tabs">
            <button :class="{ active: activeObjTab === 'table' }" @click="activeObjTab = 'table'">
              <i class="fas fa-table"></i> 表 <span class="count">{{ currentTables.length }}</span>
            </button>
            <button :class="{ active: activeObjTab === 'view' }" @click="activeObjTab = 'view'">
              <i class="fas fa-eye"></i> 视图 <span class="count">{{ currentViews.length }}</span>
            </button>
            <button :class="{ active: activeObjTab === 'procedure' }" @click="activeObjTab = 'procedure'">
              <i class="fas fa-cogs"></i> 存储过程 <span class="count">{{ currentProcedures.length }}</span>
            </button>
          </div>
        </div>
        <div class="panel-body">
          <div class="search-box">
            <input v-model="objSearch" type="text" placeholder="过滤对象名称..." />
          </div>
          <div class="obj-list">
            <div
              v-for="obj in filteredObjects"
              :key="obj.name"
              class="obj-item"
              :class="{ active: selectedObjName === obj.name && activeObjTab === selectedObjTab }"
              @click="selectObject(obj)"
            >
              <i :class="objectIcon(activeObjTab)"></i>
              <span class="obj-name">{{ obj.name }}</span>
              <span v-if="activeObjTab === 'table' && obj.rowCnt !== undefined" class="obj-rows">{{ obj.rowCnt }}行</span>
            </div>
            <div v-if="filteredObjects.length === 0 && !loadingSchema && !schemaError" class="empty-hint">暂无对象</div>
            <div v-if="loadingSchema" class="empty-hint">加载中...</div>
            <div v-if="schemaError" class="error-hint">
              <span>{{ schemaError }}</span>
              <button class="btn btn-sm btn-link" @click="loadSchema">重试</button>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <button class="btn btn-primary btn-sm" @click="openCreateDialog">
            <i class="fas fa-plus"></i> 新建{{ objLabel }}
          </button>
        </div>
      </div>

      <!-- 右侧详情/DDL -->
      <div class="right-panel-wrapper">
        <div v-if="!selectedObjName" class="empty-state">
          <i class="fas fa-arrow-left"></i>
          <p>请在左侧选择一个对象查看详情</p>
        </div>
        <template v-else>
          <div v-if="loadingDetail" class="empty-state"><p>加载详情中...</p></div>
          <template v-else>
          <div class="right-tabs">
            <button :class="{ active: activeRightTab === 'detail' }" @click="activeRightTab = 'detail'">对象信息</button>
            <button :class="{ active: activeRightTab === 'ddl' }" @click="activeRightTab = 'ddl'">DDL脚本</button>
            <button v-if="activeObjTab === 'table' || activeObjTab === 'view'" :class="{ active: activeRightTab === 'data' }" @click="activeRightTab = 'data'; loadTableData()">数据预览</button>
          </div>
          <div class="right-body">
            <!-- 对象信息 -->
            <div v-if="activeRightTab === 'detail'" class="detail-view">
              <div class="detail-header">
                <h3>{{ selectedObjName }}</h3>
                <span class="obj-tag">{{ objLabel }}</span>
              </div>
              <table v-if="activeObjTab === 'table' || activeObjTab === 'view'" class="info-table">
                <thead>
                  <tr><th>#</th><th>列名</th><th>数据类型</th><th>允许空</th><th>主键</th><th>默认值</th><th>说明</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(col, idx) in selectedColumns" :key="idx">
                    <td>{{ idx + 1 }}</td>
                    <td><strong>{{ col.name }}</strong></td>
                    <td>{{ col.type }}</td>
                    <td><span :class="col.nullable ? 'yes' : 'no'">{{ col.nullable ? 'YES' : 'NO' }}</span></td>
                    <td><i v-if="col.isPrimary" class="fas fa-key pk-icon"></i></td>
                    <td>{{ col.default || '-' }}</td>
                    <td>{{ col.comment || '-' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="procedure-detail">
                <pre>{{ selectedDef }}</pre>
              </div>
            </div>

            <!-- DDL脚本 -->
            <div v-if="activeRightTab === 'ddl'" class="ddl-view">
              <div class="ddl-toolbar">
                <span class="ddl-hint">-- {{ objLabel }}: {{ selectedObjName }}</span>
                <div>
                  <button class="btn btn-default btn-xs" @click="runAlter" v-if="activeObjTab === 'table'">
                    <i class="fas fa-edit"></i> ALTER
                  </button>
                  <button class="btn btn-primary btn-xs" @click="executeDdl" v-if="activeObjTab === 'view' || activeObjTab === 'procedure'">
                    <i class="fas fa-save"></i> 保存修改
                  </button>
                  <button class="btn btn-danger btn-xs" @click="confirmDrop">
                    <i class="fas fa-trash"></i> DROP
                  </button>
                </div>
              </div>
              <textarea
                v-model="ddlScript"
                class="ddl-editor"
                spellcheck="false"
                placeholder="DDL脚本..."
              ></textarea>
              <div v-if="activeObjTab !== 'procedure'" class="alter-panel" v-show="showAlter">
                <div class="alter-header">
                  <i class="fas fa-edit"></i> ALTER TABLE {{ selectedObjName }}
                </div>
                <div class="alter-ops">
                  <div class="alter-op-row" v-for="(op, idx) in alterOps" :key="idx">
                    <select v-model="op.action" class="alter-select">
                      <option value="ADD">ADD 列</option>
                      <option value="ALTER">ALTER 列</option>
                      <option value="DROP">DROP 列</option>
                    </select>
                    <input v-model="op.colName" class="alter-input" placeholder="列名" v-if="op.action !== 'DROP'">
                    <input v-model="op.colType" class="alter-input" placeholder="类型" v-if="op.action !== 'DROP'">
                    <button class="alter-remove" @click="alterOps.splice(idx, 1)" v-if="alterOps.length > 1">×</button>
                  </div>
                  <button class="btn btn-default btn-xs" @click="alterOps.push({ action: 'ADD', colName: '', colType: '' })">+ 添加操作</button>
                </div>
                <div class="alter-footer">
                  <button @click="showAlter = false" class="btn btn-default btn-xs">取消</button>
                  <button @click="executeAlter" class="btn btn-primary btn-xs">执行 ALTER</button>
                </div>
              </div>
            </div>

            <!-- 数据预览 -->
            <div v-if="activeRightTab === 'data'" class="data-preview">
              <div class="data-toolbar">
                <span class="data-hint">SELECT TOP 200 * FROM {{ selectedObjName }}</span>
                <button class="btn btn-default btn-xs" @click="loadTableData">
                  <i class="fas fa-sync-alt"></i> 刷新
                </button>
              </div>
              <div class="data-table-wrapper">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th v-for="col in previewColumns" :key="col">{{ col }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, ri) in previewRows" :key="ri">
                      <td v-for="col in previewColumns" :key="col">{{ row[col] !== null ? row[col] : 'NULL' }}</td>
                    </tr>
                    <tr v-if="previewRows.length === 0">
                      <td :colspan="previewColumns.length" class="empty-cell">暂无数据</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </template>
      </div>
    </div>

    <!-- 新建对象弹窗 -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal-dialog modal-wide">
        <div class="modal-header">
          <h4>新建{{ objLabel }} - {{ currentDatabase }}</h4>
          <button @click="showCreateModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>{{ objLabel }}名称 *</label>
            <input v-model="createForm.name" type="text" class="form-control" :placeholder="createFormPlaceholder">
          </div>
          <div v-if="activeObjTab === 'table'" class="form-group">
            <label>列定义</label>
            <div class="col-defs">
              <div class="col-def-row" v-for="(col, idx) in createForm.columns" :key="idx">
                <input v-model="col.name" class="col-input" placeholder="列名">
                <select v-model="col.type" class="col-type">
                  <option value="INT">INT</option>
                  <option value="BIGINT">BIGINT</option>
                  <option value="VARCHAR(50)">VARCHAR(50)</option>
                  <option value="VARCHAR(200)">VARCHAR(200)</option>
                  <option value="VARCHAR(MAX)">VARCHAR(MAX)</option>
                  <option value="NVARCHAR(100)">NVARCHAR(100)</option>
                  <option value="NVARCHAR(MAX)">NVARCHAR(MAX)</option>
                  <option value="DECIMAL(18,2)">DECIMAL(18,2)</option>
                  <option value="DATETIME">DATETIME</option>
                  <option value="BIT">BIT</option>
                  <option value="FLOAT">FLOAT</option>
                  <option value="TEXT">TEXT</option>
                </select>
                <label class="col-pk"><input type="checkbox" v-model="col.isPrimary"> 主键</label>
                <label class="col-null"><input type="checkbox" v-model="col.nullable"> 允许空</label>
                <button class="col-remove" @click="createForm.columns.splice(idx, 1)" v-if="createForm.columns.length > 1">×</button>
              </div>
              <button class="btn btn-default btn-xs" @click="createForm.columns.push({ name: '', type: 'VARCHAR(50)', nullable: true, isPrimary: false })">+ 添加列</button>
            </div>
          </div>
          <div v-if="activeObjTab === 'view'" class="form-group">
            <label>SELECT 语句 *</label>
            <textarea v-model="createForm.sql" class="form-control sql-input" rows="5" placeholder="SELECT ... FROM ..."></textarea>
          </div>
          <div v-if="activeObjTab === 'procedure'" class="form-group">
            <label>存储过程定义</label>
            <textarea v-model="createForm.sql" class="form-control sql-input" rows="10" :placeholder="procedurePlaceholder"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showCreateModal = false" class="btn btn-default">取消</button>
          <button @click="executeCreate" class="btn btn-primary">创建</button>
        </div>
      </div>
    </div>

    <!-- 连接管理弹窗 -->
    <div v-if="showConnModal" class="modal-overlay" @click.self="showConnModal = false">
      <div class="modal-dialog">
        <div class="modal-header">
          <h4>连接管理</h4>
          <button @click="showConnModal = false" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="conn-list">
            <div v-for="conn in connections" :key="conn.id" class="conn-item" :class="{ active: conn.id === currentConnId }">
              <div class="conn-info">
                <i class="fas fa-database"></i>
                <div>
                  <strong>{{ conn.name }}</strong>
                  <span>{{ conn.server }} / {{ conn.database }}</span>
                </div>
              </div>
              <button class="conn-switch-btn" @click="switchConnection(conn.id)" v-if="conn.id !== currentConnId">切换</button>
              <span v-else class="current-badge">当前</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showConnModal = false" class="btn btn-default">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { getConnections, getSchema, getTableDetail, getViewDefinition, getProcedureDefinition, executeSql } from '../api/database'

// ===== 连接管理 =====
const connections = ref([])
const currentConnId = ref('')
const showConnModal = ref(false)
const loadingConnections = ref(false)

const currentConn = computed(() => connections.value.find(c => c.id === currentConnId.value) || connections.value[0])

const loadConnections = async () => {
  loadingConnections.value = true
  try {
    const res = await getConnections()
    console.log('connections raw:', res)
    const list = res.Data || res.data || []
    console.log('connections list:', list)
    connections.value = list
    if (list.length > 0) {
      currentConnId.value = list[0].id
      // watch 会自动触发 loadSchema
    }
  } catch (e) {
    console.error('加载连接失败', e)
  } finally {
    loadingConnections.value = false
  }
}

onMounted(loadConnections)

function switchConnection(id) {
  currentConnId.value = id
  showConnModal.value = false
  selectedObjName.value = ''
}

// 切换连接时只清除选中对象（loadSchema 由 watch 自动触发）
const onSwitchConnection = (event) => {
  selectedObjName.value = ''
  // 确保 currentConnId 在 watch 前就已更新
  currentConnId.value = event.target.value
}

// 当前数据库名（保护currentConn可能为空）
const currentDatabase = computed(() => currentConn.value?.database ?? '-')
const currentServer = computed(() => currentConn.value?.server ?? '-')

// ===== 真实Schema数据 =====
const tables = ref([])
const views = ref([])
const procedures = ref([])
const loadingSchema = ref(false)
const schemaError = ref('')
const connError = ref('')   // 连接层错误（无效ID等）

const loadSchema = async () => {
  console.log('loadSchema called, currentConnId:', currentConnId.value)
  if (!currentConnId.value) return
  loadingSchema.value = true
  schemaError.value = ''
  connError.value = ''
  try {
    const res = await getSchema(currentConnId.value)
    console.log('schema raw:', res)

    // 检查 resultType
    const resultType = res.resultType ?? true
    if (resultType === false) {
      schemaError.value = res.msg || res.Msg || '获取结构失败'
      console.error('schema 错误:', schemaError.value)
      tables.value = []
      views.value = []
      procedures.value = []
      return
    }

    const data = res.data || res.Data || {}
    console.log('schema data:', data)
    tables.value = data.tables || []
    views.value = data.views || []
    procedures.value = data.procedures || []
    console.log('tables:', tables.value.length, 'views:', views.value.length, 'procedures:', procedures.value.length)
  } catch (e) {
    console.error('加载结构失败', e)
    schemaError.value = e.message || '网络请求失败'
  } finally {
    loadingSchema.value = false
  }
}

// 连接加载后自动加载schema（immediate 确保切换时也能触发）
watch(currentConnId, (newId) => {
  console.log('currentConnId watch fired:', newId)
  if (newId) loadSchema()
})

const currentTables = computed(() => tables.value)
const currentViews = computed(() => views.value)
const currentProcedures = computed(() => procedures.value)

// ===== 对象定义 =====
const activeObjTab = ref('table')
const selectedObjTab = ref('table')
const activeRightTab = ref('detail')
const objSearch = ref('')
const selectedObjName = ref('')
const showCreateModal = ref(false)
const showAlter = ref(false)
const ddlScript = ref('')
const alterOps = ref([{ action: 'ADD', colName: '', colType: '' }])

const objLabel = computed(() => {
  const map = { table: '表', view: '视图', procedure: '存储过程' }
  return map[activeObjTab.value] || '表'
})

const createFormPlaceholder = computed(() => {
  const map = { table: '例如：Employees', view: '例如：v_ActiveUsers', procedure: '例如：usp_GetReport' }
  return map[activeObjTab.value] || '请输入名称'
})

const procedurePlaceholder = `CREATE PROCEDURE [dbo].[procName]
  @param1 INT,
  @param2 NVARCHAR(100) OUTPUT
AS
BEGIN
  SET NOCOUNT ON;
  -- SQL statements
END`

const createForm = reactive({
  name: '',
  columns: [{ name: '', type: 'VARCHAR(50)', nullable: true, isPrimary: false }],
  sql: ''
})

// 模拟数据库对象
// 当前列表
const currentList = computed(() => {
  if (activeObjTab.value === 'table') return currentTables.value
  if (activeObjTab.value === 'view') return currentViews.value
  return currentProcedures.value
})

const filteredObjects = computed(() => {
  const list = currentList.value
  if (!objSearch.value) return list
  const kw = objSearch.value.toLowerCase()
  return list.filter(o => (o.name || '').toLowerCase().includes(kw))
})

const selectedObject = computed(() => {
  return currentList.value.find(o => o.name === selectedObjName.value)
})

// ===== 选中对象详情（异步加载） =====
const selectedColumns = ref([])
const selectedDef = ref('')
const previewColumns = ref([])
const previewRows = ref([])
const loadingDetail = ref(false)

function objectIcon(type) {
  return type === 'table' ? 'fas fa-table' : type === 'view' ? 'fas fa-eye' : 'fas fa-cogs'
}

async function selectObject(obj) {
  selectedObjTab.value = activeObjTab.value
  selectedObjName.value = obj.name
  activeRightTab.value = 'detail'
  showAlter.value = false
  loadingDetail.value = true

  try {
    if (activeObjTab.value === 'table') {
      const res = await getTableDetail(currentConnId.value, obj.name)
      const data = res.Data || res.data || {}
      selectedColumns.value = data.columns || []
      ddlScript.value = data.ddlScript || ''
      previewColumns.value = data.columns?.map(c => c.name) || []
      previewRows.value = (data.previewRows || []).map(r => {
        const row = {}
        if (data.columns) {
          const camelToPascal = {}
          data.columns.forEach(c => {
            camelToPascal[String(c.name).charAt(0).toLowerCase() + String(c.name).slice(1)] = c.name
          })
          for (const [key, val] of Object.entries(r)) {
            row[camelToPascal[key] || key] = val !== undefined ? val : null
          }
        }
        return row
      })
    } else if (activeObjTab.value === 'view') {
      const res = await getViewDefinition(currentConnId.value, obj.name)
      selectedDef.value = res.Data || res.data || ''
      ddlScript.value = selectedDef.value || `CREATE VIEW [dbo].[${obj.name}]\nAS\nSELECT ... FROM ...`
      selectedColumns.value = []
      // 加载视图数据预览
      try {
        const dataRes = await executeSql({ connectionId: currentConnId.value, sql: `SELECT TOP 200 * FROM [dbo].[${obj.name}]` })
        const data = dataRes.Data || dataRes.data || {}
        if (data.resultSets && data.resultSets.length > 0) {
          const rs = data.resultSets[0]
          previewColumns.value = rs.columns || []
          previewRows.value = (rs.rows || []).map(row => {
            const fixed = {}
            const camelToPascal = {}
            for (const col of (rs.columns || [])) {
              camelToPascal[col.charAt(0).toLowerCase() + col.slice(1)] = col
            }
            for (const [key, val] of Object.entries(row)) {
              fixed[camelToPascal[key] || key] = val
            }
            return fixed
          })
        }
      } catch { previewColumns.value = []; previewRows.value = [] }
    } else {
      const res = await getProcedureDefinition(currentConnId.value, obj.name)
      selectedDef.value = res.Data || res.data || ''
      ddlScript.value = selectedDef.value || `CREATE PROCEDURE [dbo].[${obj.name}]\nAS\nBEGIN\n  SET NOCOUNT ON;\n  -- TODO\nEND`
      selectedColumns.value = []
      previewColumns.value = []
      previewRows.value = []
    }
  } catch (e) {
    console.error('加载详情失败', e)
  } finally {
    loadingDetail.value = false
  }
}

async function loadTableData() {
  activeRightTab.value = 'data'
  // 视图通过执行 SELECT 语句获取数据
  if (activeObjTab.value === 'view') {
    try {
      const dataRes = await executeSql({ connectionId: currentConnId.value, sql: `SELECT TOP 200 * FROM [dbo].[${selectedObjName.value}]` })
      const data = dataRes.Data || dataRes.data || {}
      if (data.resultSets && data.resultSets.length > 0) {
        const rs = data.resultSets[0]
        previewColumns.value = rs.columns || []
        previewRows.value = (rs.rows || []).map(row => {
          const fixed = {}
          const camelToPascal = {}
          for (const col of (rs.columns || [])) {
            camelToPascal[col.charAt(0).toLowerCase() + col.slice(1)] = col
          }
          for (const [key, val] of Object.entries(row)) {
            fixed[camelToPascal[key] || key] = val
          }
          return fixed
        })
      }
    } catch { previewColumns.value = []; previewRows.value = [] }
  }
}

function openCreateDialog() {
  createForm.name = ''
  createForm.columns = [{ name: '', type: 'VARCHAR(50)', nullable: true, isPrimary: false }]
  createForm.sql = ''
  showCreateModal.value = true
}

function executeCreate() {
  // 新建对象通过SQL执行
  let sql = ''
  if (activeObjTab.value === 'table') {
    const validCols = createForm.columns.filter(c => c.name.trim())
    if (validCols.length === 0) { $message.warning('请至少定义一个列'); return }
    const cols = validCols.map(c => {
      const nullable = c.nullable ? 'NULL' : 'NOT NULL'
      const pk = c.isPrimary ? ' PRIMARY KEY' : ''
      return `    [${c.name.trim()}] ${c.type} ${nullable}${pk}`
    }).join(',\n')
    sql = `CREATE TABLE [dbo].[${createForm.name.trim()}] (\n${cols}\n)`
  } else if (activeObjTab.value === 'view') {
    if (!createForm.sql.trim()) { $message.warning('请输入SELECT语句'); return }
    sql = `CREATE VIEW [dbo].[${createForm.name.trim()}]\nAS\n${createForm.sql.trim()}`
  } else {
    if (!createForm.sql.trim()) { $message.warning('请输入存储过程定义'); return }
    sql = createForm.sql.trim()
  }

  // 通过 execute API 执行
  executeSql({ connectionId: currentConnId.value, sql }).then(() => {
    showCreateModal.value = false
    loadSchema()
  }).catch(e => {
    $message.error('创建失败: ' + (e.message || e))
  })
}

async function confirmDrop() {
  if (!selectedObjName.value) return
  const typeMap = { table: 'TABLE', view: 'VIEW', procedure: 'PROCEDURE' }
  const type = typeMap[activeObjTab.value] || 'TABLE'
  if (await $confirm(`确定要 DROP ${objLabel.value} [${selectedObjName.value}] 吗？此操作不可撤销！`)) {
    const sql = `DROP ${type} [dbo].[${selectedObjName.value}]`
    executeSql({ connectionId: currentConnId.value, sql }).then(() => {
      selectedObjName.value = ''
      selectedColumns.value = []
      loadSchema()
    }).catch(e => {
      $message.error('删除失败: ' + (e.message || e))
    })
  }
}

function runAlter() {
  showAlter.value = !showAlter.value
  alterOps.value = [{ action: 'ADD', colName: '', colType: '' }]
}

function executeDdl() {
  if (!selectedObjName.value || !ddlScript.value.trim()) { $message.warning('DDL脚本为空'); return }
  executeSql({ connectionId: currentConnId.value, sql: ddlScript.value.trim() }).then(() => {
    $message.success('DDL执行成功')
    // 重新加载schema
    loadSchema()
  }).catch(e => {
    $message.error('DDL执行失败: ' + (e.message || e))
  })
}

function executeAlter() {
  if (!selectedObject.value) return
  const obj = selectedObject.value
  // Build ALTER TABLE SQL
  let sql = `ALTER TABLE [dbo].[${obj.name}]\n`
  const clauses = []
  alterOps.value.forEach(op => {
    if (op.action === 'ADD' && op.colName.trim()) {
      clauses.push(`ADD [${op.colName.trim()}] ${op.colType || 'VARCHAR(50)'} NULL`)
    } else if (op.action === 'ALTER' && op.colName.trim() && op.colType) {
      clauses.push(`ALTER COLUMN [${op.colName.trim()}] ${op.colType}`)
    } else if (op.action === 'DROP' && op.colName.trim()) {
      clauses.push(`DROP COLUMN [${op.colName.trim()}]`)
    }
  })
  if (clauses.length === 0) { $message.info('没有有效的修改操作'); return }
  sql += clauses.join(',\n')

  executeSql({ connectionId: currentConnId.value, sql }).then(() => {
    showAlter.value = false
    // Reload table detail
    selectObject({ name: obj.name })
  }).catch(e => {
    $message.error('修改失败: ' + (e.message || e))
  })
}

// ===== 监听tab切换 =====
watch(activeObjTab, () => {
  objSearch.value = ''
  selectedObjName.value = ''
  selectedColumns.value = []
  showAlter.value = false
})
</script>

<style scoped>
.view-container {
  padding: 12px;
  background: #f5f7fa;
  min-height: calc(94vh - 110px);
  max-height: calc(94vh - 110px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 连接栏 */
.conn-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #1f2937;
  border-radius: 6px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.conn-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #d1d5db;
  font-size: 12px;
}

.conn-bar-left i { color: #10b981; }

.conn-label { color: #9ca3af; }

.conn-select {
  padding: 4px 8px;
  border: 1px solid #374151;
  border-radius: 4px;
  background: #374151;
  color: #e5e7eb;
  font-size: 12px;
  outline: none;
  cursor: pointer;
}

.conn-server {
  color: #6b7280;
  font-size: 11px;
}

.conn-btn {
  width: 26px;
  height: 26px;
  border: 1px solid #374151;
  border-radius: 4px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.conn-btn:hover { background: #374151; color: #e5e7eb; }

.conn-bar-right { display: flex; align-items: center; gap: 6px; }
.conn-tag { font-size: 10px; padding: 2px 8px; border-radius: 3px; }
.default-tag { background: #1b4332; color: #10b981; }
.ds-tag { background: #1e3a5f; color: #60a5fa; }

/* 主区域 */
.main-area {
  display: flex;
  gap: 10px;
  flex: 1;
  min-height: 0;
}

.left-panel {
  flex: 0 0 360px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header { flex-shrink: 0; }

.obj-tabs {
  display: flex;
  border-bottom: 1px solid #e8ecf1;
}

.obj-tabs button {
  flex: 1;
  padding: 9px 4px;
  border: none;
  background: none;
  font-size: 11px;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.15s;
  border-bottom: 2px solid transparent;
}

.obj-tabs button:hover { color: #374151; background: #f9fafb; }
.obj-tabs button.active { color: #10b981; border-bottom-color: #10b981; font-weight: 600; }

.obj-tabs button .count {
  font-size: 10px;
  background: #f3f4f6;
  color: #6b7280;
  padding: 1px 6px;
  border-radius: 8px;
}

.obj-tabs button.active .count { background: #d1fae5; color: #059669; }

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 6px;
}

.search-box {
  margin-bottom: 6px;
}

.search-box input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 11px;
  outline: none;
}

.search-box input:focus { border-color: #10b981; }

.obj-list {  }

.obj-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.1s;
}

.obj-item:hover { background: #f3f4f6; }
.obj-item.active { background: #ecfdf5; color: #059669; }

.obj-item i { color: #9ca3af; font-size: 11px; width: 14px; text-align: center; }
.obj-item.active i { color: #10b981; }

.obj-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.obj-rows {
  font-size: 10px;
  color: #9ca3af;
}

.empty-hint {
  text-align: center;
  padding: 20px;
  color: #9ca3af;
  font-size: 12px;
}

.error-hint {
  text-align: center;
  padding: 12px;
  color: #f87171;
  font-size: 11px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.error-hint .btn-link { color: #60a5fa; padding: 2px 6px; font-size: 11px; }

.panel-footer {
  padding: 8px;
  border-top: 1px solid #e8ecf1;
  flex-shrink: 0;
  display: flex;
}

.panel-footer .btn { flex: 1; }

/* 右侧面板 */
.right-panel-wrapper {
  flex: 1;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  gap: 8px;
}

.empty-state i { font-size: 28px; opacity: 0.4; }
.empty-state p { font-size: 13px; }

.right-tabs {
  display: flex;
  border-bottom: 1px solid #e8ecf1;
  flex-shrink: 0;
}

.right-tabs button {
  padding: 8px 16px;
  border: none;
  background: none;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
}

.right-tabs button:hover { color: #374151; }
.right-tabs button.active { color: #10b981; border-bottom-color: #10b981; font-weight: 600; }

.right-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

/* 详情页 */
.detail-view { }
.detail-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.detail-header h3 { margin: 0; font-size: 16px; color: #1f2937; }

.obj-tag {
  font-size: 11px;
  padding: 2px 8px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 10px;
}

.info-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.info-table th {
  padding: 7px 8px;
  text-align: left;
  background: #f8fafc;
  color: #6b7280;
  font-size: 10px;
  font-weight: 600;
  border-bottom: 1px solid #e8ecf1;
}

.info-table td {
  padding: 7px 8px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.yes { color: #10b981; }
.no { color: #ef4444; }

.pk-icon { color: #f59e0b; font-size: 10px; }

.procedure-detail pre {
  background: #1e293b;
  color: #e2e8f0;
  padding: 14px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  font-family: 'Consolas', 'Courier New', monospace;
}

/* DDL */
.ddl-view { }

.ddl-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.ddl-hint { font-size: 11px; color: #9ca3af; }

.ddl-editor {
  width: 100%;
  min-height: 200px;
  padding: 12px;
  background: #1e293b;
  color: #e2e8f0;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-family: 'Consolas', 'Courier New', monospace;
  line-height: 1.6;
  resize: vertical;
  outline: none;
  tab-size: 2;
}

/* ALTER面板 */
.alter-panel {
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.alter-header {
  padding: 8px 12px;
  background: #f0fdf4;
  color: #059669;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid #d1fae5;
}

.alter-ops { padding: 10px; }

.alter-op-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.alter-select {
  width: 90px;
  padding: 4px 6px;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  font-size: 11px;
  outline: none;
}

.alter-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  font-size: 11px;
  outline: none;
}

.alter-input:focus, .alter-select:focus { border-color: #10b981; }

.alter-remove {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 3px;
  background: #fee2e2;
  color: #ef4444;
  cursor: pointer;
  font-size: 12px;
}

.alter-footer {
  padding: 8px 10px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  background: #f9fafb;
}

/* 数据预览 */
.data-preview { }

.data-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.data-hint { font-size: 11px; color: #9ca3af; font-family: monospace; }

.data-table-wrapper { max-height: 400px; overflow: auto; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.data-table th {
  padding: 6px 10px;
  background: #f8fafc;
  color: #374151;
  font-weight: 600;
  font-size: 11px;
  text-align: left;
  border: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-table td {
  padding: 5px 10px;
  border: 1px solid #f3f4f6;
  color: #374151;
  white-space: nowrap;
}

.data-table tr:hover td { background: #f9fafb; }

.empty-cell { text-align: center; color: #9ca3af; padding: 20px !important; }

/* 弹窗 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: #fff;
  border-radius: 6px;
  width: 480px;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-dialog.modal-wide { width: 700px; }

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #e8ecf1;
  flex-shrink: 0;
}

.modal-header h4 { margin: 0; font-size: 14px; color: #1f2937; }

.modal-close {
  width: 24px; height: 24px; border: none; background: none;
  font-size: 18px; color: #9ca3af; cursor: pointer;
}

.modal-body { padding: 14px; overflow-y: auto; flex: 1; }

.modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 10px 14px; border-top: 1px solid #e8ecf1; flex-shrink: 0;
}

.form-group { margin-bottom: 12px; }

.form-group label {
  display: block; font-size: 12px; font-weight: 500;
  color: #374151; margin-bottom: 4px;
}

.form-control {
  width: 100%; padding: 7px 10px; border: 1px solid #d1d5db;
  border-radius: 4px; font-size: 12px; outline: none; box-sizing: border-box;
}

.form-control:focus { border-color: #10b981; }

.sql-input {
  font-family: 'Consolas', 'Courier New', monospace;
  background: #1e293b;
  color: #e2e8f0;
  line-height: 1.6;
}

/* 列定义 */
.col-defs { margin-bottom: 8px; }

.col-def-row {
  display: flex; align-items: center; gap: 6px; margin-bottom: 6px;
}

.col-input { flex: 1; padding: 4px 8px; border: 1px solid #d1d5db; border-radius: 3px; font-size: 11px; outline: none; }
.col-input:focus { border-color: #10b981; }

.col-type { width: 130px; padding: 4px 6px; border: 1px solid #d1d5db; border-radius: 3px; font-size: 11px; outline: none; }

.col-pk, .col-null {
  font-size: 11px; color: #6b7280; display: flex; align-items: center; gap: 3px; cursor: pointer; white-space: nowrap;
}

.col-pk input, .col-null input { width: 13px; height: 13px; accent-color: #10b981; }

.col-remove {
  width: 22px; height: 22px; border: none; border-radius: 3px;
  background: #fee2e2; color: #ef4444; cursor: pointer; font-size: 12px;
}

/* 连接管理 */
.conn-list { }

.conn-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 8px;
}

.conn-item.active { border-color: #10b981; background: #f0fdf4; }

.conn-info { display: flex; align-items: center; gap: 10px; }

.conn-info i { color: #9ca3af; font-size: 18px; }

.conn-info strong { display: block; font-size: 13px; color: #1f2937; }

.conn-info span { font-size: 11px; color: #9ca3af; }

.conn-switch-btn {
  padding: 4px 12px; border: 1px solid #10b981; border-radius: 4px;
  background: #fff; color: #10b981; font-size: 11px; cursor: pointer;
}

.conn-switch-btn:hover { background: #10b981; color: #fff; }

.current-badge {
  padding: 3px 10px; background: #d1fae5; color: #059669;
  border-radius: 10px; font-size: 11px; font-weight: 500;
}

/* 按钮 */
.btn { padding: 6px 14px; border-radius: 4px; font-size: 12px; cursor: pointer; }
.btn-default { background: #fff; border: 1px solid #d1d5db; color: #374151; }
.btn-default:hover { background: #f9fafb; }
.btn-primary { background: #10b981; border: none; color: #fff; }
.btn-primary:hover { background: #059669; }
.btn-danger { background: #ef4444; border: none; color: #fff; }
.btn-danger:hover { background: #dc2626; }
.btn-xs { padding: 3px 8px; font-size: 11px; }
.btn-sm { padding: 5px 12px; font-size: 12px; }
</style>
